import { DatabaseFieldType } from '@databases/types/database';
import { DatabaseFilterType } from '@databases/types/database/view';
import { isEqual } from 'lodash-es';

import { cheerioIterator } from '../../lib/dom';
import {
  encodeHtml,
  generateId,
  getFallbackHyperlinkLabel,
  isValidHyperlinkUrl,
  makeValidHyperlink,
} from '../../utils';
import type { FieldType } from '../field-types';

const hyperlinkFieldType: FieldType<DatabaseFieldType.HYPERLINK> = {
  supportedFilterTypes: [
    DatabaseFilterType.EQUALS,
    DatabaseFilterType.CONTAINS,
    DatabaseFilterType.EMPTY,
  ],

  isCsvImportSupported: true,

  renderExportView: ({ value }) => {
    return value
      .map(makeValidHyperlink)
      .map((v) => {
        if (v.isLabelExplicit) {
          return `<a href="${encodeHtml(v.url)}">${encodeHtml(v.label || v.url)}</a>`;
        } else {
          return `<a href="${encodeHtml(
            v.url
          )}" data-card-appearance="inline">${encodeHtml(v.url)}</a>`;
        }
      })
      .join('<br/>');
  },

  parseFromHtml: (el) => {
    return [...cheerioIterator(el.find('a[href]'))].flatMap((a) => {
      const url = a.attr('href') ?? '';
      if (isValidHyperlinkUrl(url)) {
        const text = a.text();
        const isLabelExplicit = a.attr('data-card-appearance') !== 'inline';
        return [
          {
            _id: generateId(),
            url,
            label: text && (isLabelExplicit || text !== url) ? text : undefined,
            isLabelExplicit,
          },
        ];
      }
      return [];
    });
  },

  getInitialValue: () => [],

  isEmptyValue: ({ value }) =>
    value.filter((hyperlink) => Boolean(hyperlink.url)).length === 0,

  isEqualValue: ({ value: value1 }, { value: value2 }) =>
    isEqual(
      value1.map(makeValidHyperlink).map((v) => v.url),
      value2.map(makeValidHyperlink).map((v) => v.url)
    ),

  getStringRepresentation: ({ value }) => {
    return value
      .map((v) => makeValidHyperlink(v))
      .map((v) => v.label || getFallbackHyperlinkLabel(v.url))
      .join('\n');
  },

  convertFrom: {
    [DatabaseFieldType.TEXT]: ({ value }) => {
      if (isValidHyperlinkUrl(value)) {
        return [{ _id: generateId(), url: value }];
      } else {
        return [];
      }
    },
  },

  convertTo: {
    [DatabaseFieldType.TEXT]: ({ value }) => {
      return value
        .map(makeValidHyperlink)
        .map((v) => v.label ?? v.url)
        .join('\n');
    },
  },
};

export default hyperlinkFieldType;
